import { useState, useEffect, useMemo, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@cp/src/store';
import { IOption } from '@cp/src/components/AutocompleteDropdown/interfaces';
import EmbeddedPlayer from './EmbeddedPlayer';
import DetailsPopper from './DetailsPopper';
import Footer from './Footer';
import PreAuditBanner from './PreAudit/PreAuditBanner';
import SingleSelectQuestions from './Questions/SingleSelectQuestions';
import ViolationQuestion from './Questions/ViolationQuestion';
import PreAuditValuesSection from './PreAudit/PreAuditValuesSection';
import MultiSelectErrorSection from './Questions/MultiSelectErrorSection';
import {
  ApiAuditTrailAnswer,
  AuditAnswer,
  AuditFeedbackFormProps,
  AuditQuestionConfig,
  CallingAgentAuditDetails
} from '../interface';
import { submitQcTeamLeadAudit, submitQcAgentAudit } from '../action';
import { Roles } from '../../auth/constants/AuthConstants';
import { AuditStatus } from './types';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { AuditorType, QuestionTag, QuestionType, OptionTag } from './auditEnums';

const AuditFeedbackForm: FC<AuditFeedbackFormProps> = ({
  onSubmit,
  readOnly = false,
  isDrawerOpen = true
}) => {
  const dispatch = useDispatch();

  const auditAssignmentReferenceId =
    useSelector((state: RootState) => state.callAudit?.selectedAuditAssignmentReferenceId) || '';
  const tlReferenceId =
    useSelector((state: RootState) => state.common?.userData?.referenceId) || '';
  const userRoles = useSelector((state: RootState) => state?.common?.userData?.roles);
  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;
  const isSidebarLoading = useSelector((state: RootState) => state.callAudit?.isSidebarLoading);

  const isQcAgent = userRoles?.includes(Roles.ROLE_PX_AUDIT_AGENT);
  const isTeamLead = userRoles?.includes(Roles.ROLE_PX_AUDIT_TEAM_LEAD);

  const auditQuestionnaire = (auditDetails?.auditConfig?.auditQuestionnaire ||
    []) as AuditQuestionConfig[];
  const auditTrail = auditDetails?.auditTrail || [];
  const recordingUrl = auditDetails?.recordingUrl || '';
  const auditStatus = auditDetails?.auditStatus || '';

  const dsAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.DS),
    [auditTrail]
  );

  const formPrefilledAnswers = useMemo(() => {
    const qcTeamLeadAuditTrail = auditTrail.find(t => t.auditor === AuditorType.QC_TEAM_LEAD);
    const qcAgentAuditTrail = auditTrail.find(t => t.auditor === AuditorType.QC_AGENT);
    if (qcTeamLeadAuditTrail) {
      return (qcTeamLeadAuditTrail.answers || []) as unknown as ApiAuditTrailAnswer[];
    }
    if (qcAgentAuditTrail) {
      return (qcAgentAuditTrail.answers || []) as unknown as ApiAuditTrailAnswer[];
    }
    return (dsAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  }, [auditTrail, dsAuditTrail]);

  const singleSelectQuestions = useMemo(
    () =>
      auditQuestionnaire.filter(
        q =>
          q.question_type === QuestionType.SINGLE_SELECT &&
          q.question_tag !== QuestionTag.IS_VIOLATION
      ),
    [auditQuestionnaire]
  );

  const violationQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.IS_VIOLATION),
    [auditQuestionnaire]
  );

  const violationReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.VIOLATION_REASON_LIST),
    [auditQuestionnaire]
  );

  const clearanceReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.CLEARANCE_REASON_LIST),
    [auditQuestionnaire]
  );

  const [answers, setAnswers] = useState<Record<number, number[]>>({});

  const isViolationFound = useMemo(() => {
    if (!violationQuestion) return false;
    const selectedOptionIds = answers[violationQuestion.question_id] || [];
    const selectedOption = violationQuestion.options.find(opt =>
      selectedOptionIds.includes(opt.option_id)
    );
    return selectedOption?.option_tag === OptionTag.VIOLATION_FOUND;
  }, [violationQuestion, answers]);

  const multiSelectQuestion = useMemo(() => {
    return isViolationFound ? violationReasonQuestion : clearanceReasonQuestion;
  }, [isViolationFound, violationReasonQuestion, clearanceReasonQuestion]);

  useEffect(() => {
    if (formPrefilledAnswers.length > 0) {
      const initialAnswers: Record<number, number[]> = {};
      formPrefilledAnswers.forEach((answer: ApiAuditTrailAnswer) => {
        initialAnswers[answer.question_id] = answer.selected_option_ids;
      });
      setAnswers(initialAnswers);
    }
  }, [formPrefilledAnswers]);

  const getSelectedOption = (questionId: number): string => {
    const selectedIds = answers[questionId];
    return selectedIds?.length ? String(selectedIds[0]) : '';
  };

  const handleSingleSelectChange = (questionId: number, optionId: number) => {
    setAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: [optionId] };
      if (violationQuestion && questionId === violationQuestion.question_id) {
        if (violationReasonQuestion) delete newAnswers[violationReasonQuestion.question_id];
        if (clearanceReasonQuestion) delete newAnswers[clearanceReasonQuestion.question_id];
      }
      return newAnswers;
    });
  };

  const handleMultiSelectChange = (questionId: number, options: IOption[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: options.map(opt => parseInt(opt.value, 10))
    }));
  };

  const getSelectedMultiOptions = (
    questionId: number,
    question: AuditQuestionConfig
  ): IOption[] => {
    const selectedIds = answers[questionId] || [];
    return selectedIds
      .map(id => {
        const option = question.options.find(opt => opt.option_id === id);
        return option ? { value: String(option.option_id), label: option.option_text } : null;
      })
      .filter((opt): opt is IOption => opt !== null);
  };

  const isSubmitDisabled = useMemo((): boolean => {
    if (auditStatus === AuditStatus.PENDING) return false;
    if (auditStatus === AuditStatus.AGENT_CLEARED || auditStatus === AuditStatus.AGENT_VIOLATION) {
      if (isQcAgent) return true;
      if (isTeamLead) return false;
    }
    return true;
  }, [auditStatus, isQcAgent, isTeamLead]);

  const isFormDisabled = isSubmitDisabled || readOnly;

  const handleSubmit = () => {
    const formattedAnswers: AuditAnswer[] = Object.entries(answers).map(
      ([questionId, selectedOptionIds]) => ({
        questionId: parseInt(questionId, 10),
        selectedOptionIds
      })
    );

    const successCallback = () => onSubmit?.(formattedAnswers);

    if (isQcAgent) {
      dispatch(
        submitQcAgentAudit(
          auditAssignmentReferenceId,
          { answers: formattedAnswers },
          successCallback
        )
      );
    } else {
      dispatch(
        submitQcTeamLeadAudit(
          auditAssignmentReferenceId,
          { answers: formattedAnswers, tlReferenceId },
          successCallback
        )
      );
    }
  };

  if (isSidebarLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoaderIcon
          size={40}
          borderWidth={4}
          borderColor="var(--navi-color-gray-border)"
          fillColor="var(--navi-color-blue-base)"
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full bg-[var(--bg-primary)] border-b border-[var(--grayscale-6)] flex-shrink-0">
        <EmbeddedPlayer url={recordingUrl} />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <DetailsPopper isTDME={readOnly} isDrawerOpen={isDrawerOpen} />

        {isQcAgent && dsAuditTrail && <PreAuditBanner />}

        <SingleSelectQuestions
          questions={singleSelectQuestions}
          getSelectedOption={getSelectedOption}
          handleSingleSelectChange={handleSingleSelectChange}
          isFormDisabled={isFormDisabled}
          readOnly={readOnly}
        />

        {violationQuestion && (
          <ViolationQuestion
            question={violationQuestion}
            getSelectedOption={getSelectedOption}
            handleSingleSelectChange={handleSingleSelectChange}
            isFormDisabled={isFormDisabled}
            readOnly={readOnly}
          />
        )}

        <PreAuditValuesSection readOnly={readOnly} />

        {multiSelectQuestion && !isFormDisabled && (
          <MultiSelectErrorSection
            question={multiSelectQuestion}
            selectedOptions={getSelectedMultiOptions(
              multiSelectQuestion.question_id,
              multiSelectQuestion
            )}
            onSelectionChange={options =>
              handleMultiSelectChange(multiSelectQuestion.question_id, options)
            }
          />
        )}
      </div>

      {!readOnly && <Footer handleSubmit={handleSubmit} isSubmitDisabled={isSubmitDisabled} />}
    </div>
  );
};

export default AuditFeedbackForm;
